<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'
import { usePageChromeState, type ChromeVariant } from '@/composables/usePageChrome'
import type { ChromeLink } from '@/composables/usePageChrome'
import ChromeAscentButton from './ChromeAscentButton.vue'
import ChromeTitle from './ChromeTitle.vue'
import ChromeLinksRegion from './ChromeLinksRegion.vue'
import ChromeToolsRegion from './ChromeToolsRegion.vue'

/**
 * App Chrome v1 — the single, shared top-chrome shell.
 *
 * Shape is declared statically per route (`meta.chrome`); dynamic content
 * (title, runtime ascent target) flows in through `usePageChrome`. The view
 * fills the tools region by teleporting into `#app-chrome-tools`. This is the
 * "shared shell with a sane default that each view fills via slots" from the
 * repo's Design Principles — ownership is filling slots, not painting a canvas.
 */
const props = defineProps<{ isMobile?: boolean }>()

const route = useRoute()
const router = useRouter()
const state = usePageChromeState()

// Variant precedence: runtime override → route meta → hidden. A legacy
// `hideAppChrome` is honored as an explicit 'hidden' during migration.
const variant = computed<ChromeVariant>(() => {
  if (route.meta.hideAppChrome) return 'hidden'
  return state.variant ?? route.meta.chrome ?? 'hidden'
})

const visible = computed(() => variant.value !== 'hidden')
const hideHeaderOnMobile = computed(() => props.isMobile && route.meta.hideHeaderOnMobile === true)
const hasHeader = computed(() => variant.value.includes('header') && !hideHeaderOnMobile.value)
const hasTools = computed(() => variant.value.includes('tools'))
const recessive = computed(() => route.meta.chromeRecessive === true)
const showAscentOnMobileRecessive = computed(() => route.meta.showAscentOnMobileRecessive === true)
const isHomeRoute = computed(() => route.name === 'home')
const mobileHomeTitleOnly = computed(() => props.isMobile && isHomeRoute.value)
const showTools = computed(() => hasTools.value && !mobileHomeTitleOnly.value)

// Title source precedence: a runtime override (live values like a character
// name) wins; otherwise fall back to the route's static `meta.title`. This lets
// static-title list views declare chrome purely in route meta — no composable
// call needed — while dynamic views still drive the title via usePageChrome.
const title = computed(() => state.title ?? route.meta.title ?? null)

// Ascent target resolution order:
//   1. runtime `state.ascentTo` (e.g. conversation → its loaded character)
//   2. static `meta.ascent = { to }`
//   3. named `meta.ascent = { name, paramKeys }` resolved against live params
//   4. none (root routes omit `meta.ascent`)
const ascentTarget = computed<RouteLocationRaw | null>(() => {
  if (state.ascentTo) return state.ascentTo as RouteLocationRaw

  const ascent = route.meta.ascent
  if (!ascent) return null

  if ('to' in ascent) return ascent.to

  const params: Record<string, string | string[]> = {}
  for (const key of ascent.paramKeys ?? []) {
    if (key in route.params) params[key] = route.params[key]
  }
  return { name: ascent.name, params }
})

// Lateral links region. Cross-cutting like ascent (not a variant): a curated
// row of type-level "sideways" destinations. Runtime links win over static
// `meta.links`, mirroring the ascent precedence above. Home uses this for its
// broad section launcher; most routes declare none.
const links = computed<ChromeLink[]>(() => {
  if (mobileHomeTitleOnly.value) return []
  if (state.links) return state.links as ChromeLink[]
  return route.meta.links ?? []
})

// Name the destination for the ascent control's aria-label by reading the
// target route's own `meta.title` — so it reads "Back to Characters", not "Back".
const ascentLabel = computed<string | undefined>(() => {
  if (!ascentTarget.value) return undefined
  try {
    const resolved = router.resolve(ascentTarget.value)
    return resolved.meta?.title
  } catch {
    return undefined
  }
})

// Ascent is a cross-cutting flag, not a variant: shown wherever a parent
// exists, unless the view opts out (`ascentEnabled === false`) or we're on a
// mobile recessive surface (defer to the OS back gesture there).
const showAscent = computed(() => {
  if (!ascentTarget.value) return false
  if (state.ascentEnabled === false) return false
  if (props.isMobile && recessive.value && !showAscentOnMobileRecessive.value) return false
  return true
})
</script>

<template>
  <header
    v-if="visible"
    class="app-chrome"
    :data-variant="variant"
    :data-recessive="recessive ? '' : undefined"
    :data-mobile="props.isMobile ? '' : undefined"
    :data-home="isHomeRoute ? '' : undefined"
  >
    <div class="app-chrome__lead">
      <ChromeAscentButton
        v-if="showAscent && ascentTarget"
        :to="ascentTarget"
        :label="ascentLabel"
      />
      <ChromeTitle
        v-if="hasHeader"
        :title="title"
        :subtitle="state.subtitle"
        :prominent="isHomeRoute"
      />
      <ChromeLinksRegion
        v-if="links.length"
        :links="links"
      />
    </div>

    <ChromeToolsRegion v-if="showTools">
      <slot name="tools" />
    </ChromeToolsRegion>
  </header>
</template>

<style scoped>
.app-chrome {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 950;
  display: flex;
  align-items: center;
  gap: 8px;
  height: var(--chrome-height, 56px);
  padding: 0 12px;
  box-sizing: border-box;
}

/* Solid (operational) chrome: a surface that sits in the layout. */
.app-chrome:not([data-recessive]) {
  background: var(--theme-surface-solid);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid var(--theme-border);
}

/* Recessive chrome floats over the view's own background — transparent, no
   border — for immersive sinks (a conversation) where content sits beneath. */
.app-chrome[data-recessive] {
  background: transparent;
  border-bottom: 1px solid transparent;
  pointer-events: none;
}

/* …but the controls inside it must stay clickable through the pass-through.
   The ascent cluster covers both the arrow and its home reveal. */
.app-chrome[data-recessive] :deep(.chrome-ascent-cluster),
.app-chrome[data-recessive] :deep(.chrome-tools) {
  pointer-events: auto;
}

.app-chrome__lead {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.app-chrome[data-home]:not([data-mobile]) {
  padding-left: clamp(96px, 8vw, 160px);
}
</style>
